import { CARD_WIDTH } from '../design/tokens';
import { bandFor } from '../entity/bands';
import { kindByFolder } from '../vault/kinds';
import type { TagColorId } from '../design/tagPalette';
import { arcTitleOf, cardHeight, stickyHeight } from '../plane/cardSize';
import type { PlanePlacement, StickyTint } from '../plane/planeKey';

/**
 * A nook's plane in miniature: its placements as shapes, by arrangement alone.
 * No text, no image — what makes one nook recognisable from another is where
 * things sit.
 */

export interface MiniShape {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** A note is drawn as a sticky in its tint; everything else as a card. */
  sticky: boolean;
  tint: StickyTint;
  /** A tag colour chosen for the sticky; wins over [tint]. */
  tagTint?: TagColorId;
  /** False for a sticky left at the default tint, which a tagged nook's own
   *  colour then takes over. */
  tintChosen: boolean;
}

/** The width a placement with none of its own takes, and the height of a note with no size and no
 *  `~N`. A card that is not a note is as tall as its `~N` lines, or its kind's estimate. */
const CARD_W = CARD_WIDTH;
const STICKY = { w: 220, h: 110 };

export function miniatureShapes(
  plane: readonly PlanePlacement[],
  kindAt: (path: string) => string | null,
): MiniShape[] {
  return plane.map((p) => {
    const arc = kindByFolder(p.ref) === 'arc' || kindAt(p.ref) === 'arc';
    // A tint rides only a note's entry; otherwise the catalog says what it is.
    const sticky = !arc && (p.tint !== undefined || p.tagTint !== undefined || bandFor(kindAt(p.ref), p.ref) === 'notes');
    return {
      key: p.anchors.length ? `${p.ref}#${p.anchors[0]}` : p.ref,
      x: p.x,
      y: p.y,
      w: p.w ?? (sticky ? STICKY.w : CARD_W),
      h: sticky ? stickyHeight(p, STICKY.h) : arc ? cardHeight(p, 'arc', arcTitleOf(p.ref)) : cardHeight(p, 'recording'),
      sticky,
      tint: p.tint ?? 'warm',
      ...(p.tagTint ? { tagTint: p.tagTint } : {}),
      tintChosen: p.tint !== undefined || p.tagTint !== undefined,
    };
  });
}

/** Room left around the arrangement, per side, as a share of its size. */
const PAD = 0.12;
/** A lone shape stands further back: fitted as tight as a group, it fills the band edge to edge. */
const PAD_ALONE = 0.4;
/** No shape draws smaller than this on its short side (a fifth of the box's
 *  height in a small one): one far-off card must not shrink the rest to specks.
 *  A floor on the drawn shape, never on the scale, so the whole arrangement
 *  always fits. */
const MIN_SHORT_SIDE = 8;

/**
 * How the shapes map into a box: one uniform scale, and the offset that
 * centres the arrangement. Null when there is nothing to draw.
 */
export function fitMiniature(
  shapes: readonly MiniShape[],
  box: { w: number; h: number },
): { scale: number; x: number; y: number; min: number; w: number; h: number } | null {
  if (shapes.length === 0) return null;
  const left = Math.min(...shapes.map((s) => s.x));
  const top = Math.min(...shapes.map((s) => s.y));
  const right = Math.max(...shapes.map((s) => s.x + s.w));
  const bottom = Math.max(...shapes.map((s) => s.y + s.h));
  const width = Math.max(right - left, 1);
  const height = Math.max(bottom - top, 1);

  const pad = shapes.length === 1 ? PAD_ALONE : PAD;
  const fit = Math.min(box.w / (width * (1 + 2 * pad)), box.h / (height * (1 + 2 * pad)));
  const scale = fit;

  return {
    scale,
    x: box.w / 2 - ((left + right) / 2) * scale,
    y: box.h / 2 - ((top + bottom) / 2) * scale,
    min: Math.min(MIN_SHORT_SIDE, box.h / 5),
    w: box.w,
    h: box.h,
  };
}

/** Where a shape draws under [fit]: scaled in place, grown about its own
 *  centre where its short side would fall under the floor, and slid back
 *  inside the box where growing pushed it over an edge. */
export function drawnShape(
  shape: { x: number; y: number; w: number; h: number },
  fit: { scale: number; x: number; y: number; min: number; w: number; h: number },
): { left: number; top: number; width: number; height: number } {
  const grow = Math.max(1, fit.min / Math.max(Math.min(shape.w, shape.h) * fit.scale, 0.001));
  const width = Math.min(shape.w * fit.scale * grow, fit.w);
  const height = Math.min(shape.h * fit.scale * grow, fit.h);
  const cx = (shape.x + shape.w / 2) * fit.scale + fit.x;
  const cy = (shape.y + shape.h / 2) * fit.scale + fit.y;
  const inside = (start: number, size: number, room: number) => Math.min(Math.max(start, 0), room - size);
  return {
    left: inside(cx - width / 2, width, fit.w),
    top: inside(cy - height / 2, height, fit.h),
    width,
    height,
  };
}

/**
 * The height of a [boxW]-wide strip, within [minH] and [maxH], at which
 * [shapes] are drawn as large as the width allows without running out of the
 * strip at [fitMiniature]'s scale.
 */
export function stripHeight(shapes: readonly MiniShape[], boxW: number, minH: number, maxH: number): number {
  const fit = fitMiniature(shapes, { w: boxW, h: Number.MAX_VALUE });
  if (!fit) return minH;
  const pad = shapes.length === 1 ? PAD_ALONE : PAD;
  const height = Math.max(Math.max(...shapes.map((s) => s.y + s.h)) - Math.min(...shapes.map((s) => s.y)), 1);
  return Math.min(Math.max(height * (1 + 2 * pad) * fit.scale, minH), maxH);
}
